/**
 * extractErrorMessage (TASK-337, AC-5 / FR-003)
 *
 * Shared error-message extractor, unified from the duplicated copies in
 * TelephonyPage (richer variant — the contract) and DatasetUploadPage.
 *
 * Extracts a human-readable message from an axios error. Prefers the backend
 * text — a raw string body, then `detail` for DRF errors, then any string
 * values of a non-detail error body ({"field": ["msg"]}) — over the generic
 * axios `err.message` ("Request failed with status code 400"), falling back
 * to the given fallback string.
 *
 * A raw string body that looks like HTML (starts with "<" after trim) is
 * skipped: nginx 502/504 error pages must not be shown to the user verbatim.
 */
export function extractErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const maybe = err as {
      response?: { data?: unknown };
      message?: string;
    };
    const data = maybe.response?.data;
    if (
      typeof data === 'string' &&
      data.trim() !== '' &&
      !data.trim().startsWith('<')
    ) {
      return data;
    }
    if (data && typeof data === 'object') {
      const detail = (data as { detail?: unknown }).detail;
      if (typeof detail === 'string' && detail !== '') {
        return detail;
      }
      // Non-detail DRF shapes: {"field": ["msg", ...]} / {"non_field_errors": [...]}.
      const parts = Object.values(data as Record<string, unknown>)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .filter(
          (value): value is string => typeof value === 'string' && value !== ''
        );
      if (parts.length > 0) {
        return parts.join(' ');
      }
    }
    if (maybe.message) {
      return maybe.message;
    }
  }
  return fallback;
}
